import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Star, Sparkles, Loader2, Check, X } from "lucide-react";
import { useApp, API } from "../context";
import { useGuest } from "./GuestSystem";
import { celebrateSmall, celebrateMedium, celebrateLarge } from "../utils/celebrate";

/**
 * DailyStarWheel, one free spin per day for any logged-in kid.
 * Renders a collapsible card on the Home Dashboard.
 * - Shows "SPIN NOW" CTA when can_spin
 * - After spin: animates wedge pointer, reveals prize + new balance
 * - Disables and shows "Come back tomorrow!" once used
 */
const DailyStarWheel = () => {
  const { user, refreshUser } = useApp();
  const { isGuest, trackActivity } = useGuest();
  const [status, setStatus] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  // Manual dismiss (X button), for-this-session hide. The card will
  // reappear naturally tomorrow once the backend flips can_spin=true.
  const [dismissed, setDismissed] = useState(false);
  // Auto-hide timer ref for the post-spin celebration window
  const [celebrationWindow, setCelebrationWindow] = useState(false);

  const uid = user?.id || user?.email;

  const load = useCallback(async () => {
    if (!uid) return;
    try {
      const r = await axios.get(`${API}/daily-wheel/status/${encodeURIComponent(uid)}`);
      setStatus(r.data);
      if (!r.data.can_spin && r.data.last_spin) {
        setResult({ stars_won: r.data.last_spin.stars_won, label: r.data.last_spin.label, already: true });
        // Guests: if the backend remembers today's win but the local progress
        // hasn't been credited yet, mirror it once so the progress chip/session
        // summary display the correct star count after a reload.
        if (isGuest && trackActivity) {
          const dateKey = r.data.last_spin.date || r.data.last_spin.spin_date;
          const syncedKey = `isaiah_guest_wheel_synced_${dateKey || "today"}`;
          try {
            if (dateKey && !sessionStorage.getItem(syncedKey)) {
              trackActivity("star_earned", { amount: r.data.last_spin.stars_won || 0, source: "daily_wheel_reload" });
              sessionStorage.setItem(syncedKey, "1");
            }
          } catch {}
        }
      }
    } catch (e) { /* silent */ }
  }, [uid, isGuest, trackActivity]);

  useEffect(() => { load(); }, [load]);

  const spin = async () => {
    if (spinning || !uid || !status?.can_spin) return;
    setSpinning(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/daily-wheel/spin`, { user_id: uid });
      if (!r.data.success) {
        toast.info(r.data.message || "Come back tomorrow!");
        await load();
        setSpinning(false);
        return;
      }
      // Find winning wedge index for rotation animation
      const wedges = status.wedges;
      const idx = wedges.findIndex(w => w.id === r.data.result.wedge_id);
      const slice = 360 / wedges.length;
      // End pointer at the center of the winning wedge + multiple full spins
      const target = 360 * 6 + (360 - (idx * slice + slice / 2));
      setRotation(target);

      // Reveal the prize after animation completes (~3s)
      setTimeout(async () => {
        const totalWon = (r.data.stars_won || 0) + (r.data.streak_bonus || 0);
        // Guests: mirror the win into local guest progress so the timer chip,
        // session summary, and banner all reflect wheel rewards immediately.
        if (isGuest && totalWon > 0 && trackActivity) {
          trackActivity("star_earned", { amount: totalWon, source: "daily_wheel" });
        }
        setResult({
          stars_won: r.data.stars_won,
          label: r.data.result.label,
          is_jackpot: r.data.is_jackpot,
          streak_bonus: r.data.streak_bonus || 0,
          streak_after: r.data.streak_after || 0,
          streak_badge_granted: !!r.data.streak_badge_granted,
        });
        if (r.data.streak_bonus > 0) {
          toast.success(`🔥 ${r.data.streak_after}-Day Streak! +${r.data.stars_won + r.data.streak_bonus} Stars total!`, { duration: 7000 });
          // Streak milestone = cinematic burst; badge unlock layers a
          // second medium burst on top so the moment keeps celebrating.
          celebrateLarge();
          if (r.data.streak_badge_granted) {
            setTimeout(() => toast.success("👑 NEW BADGE: Week Warrior unlocked!", { duration: 6000 }), 1200);
            setTimeout(celebrateMedium, 1200);
          }
        } else if (r.data.is_jackpot) {
          toast.success(`🎉 JACKPOT! +${r.data.stars_won} Stars!`, { duration: 5000 });
          celebrateLarge();
        } else {
          toast.success(`+${r.data.stars_won} Stars! 🌟`);
          celebrateSmall();
        }
        // Enter celebration window, keep the card visible for 15s so the
        // user can read their reward + appreciate the animation, then
        // auto-hide. They can also click the X to close immediately. The
        // backend flips `can_spin` back to true at the next daily reset, at
        // which point the card re-appears on its own.
        setCelebrationWindow(true);
        setTimeout(() => setCelebrationWindow(false), 15000);
        if (refreshUser) { try { await refreshUser(); } catch {} }
        await load();
        setSpinning(false);
      }, 3200);
    } catch (e) {
      toast.error("Couldn't spin right now. Try again in a sec!");
      setSpinning(false);
    }
  };

  if (!status) return null; // don't render until loaded
  // Hide the card entirely when the user can't spin right now AND we're
  // not in the middle of the post-spin celebration window. This covers:
  //  • Cold-load after today's spin was already used (can_spin=false on mount)
  //  • Post-spin, 5.5s after the wedge reveals (celebrationWindow expires)
  //  • Manual X dismissal (dismissed=true for the session)
  // The card re-appears naturally tomorrow once the backend flips
  // can_spin back to true.
  if (dismissed) return null;
  if (!status.can_spin && !spinning && !celebrationWindow) return null;
  const wedges = status.wedges || [];
  const slice = wedges.length > 0 ? 360 / wedges.length : 0;

  return (
    <div
      className="relative rounded-2xl border-2 border-amber-500/30 overflow-hidden"
      style={{ background: "linear-gradient(135deg, rgba(251,191,36,0.12) 0%, rgba(236,72,153,0.1) 50%, rgba(168,85,247,0.12) 100%)" }}
      data-testid="daily-wheel-card"
    >
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(circle at top right, rgba(251,191,36,0.25), transparent 60%)" }} />
      {/* Close / snooze-for-today, hides the card for the session.
          Reappears naturally when tomorrow's spin unlocks. Disabled while
          the wheel is actively spinning so the user doesn't lose the
          animation mid-celebration. */}
      {!spinning && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/25 hover:bg-black/50 border border-white/10 text-white/60 hover:text-white flex items-center justify-center transition-colors"
          aria-label="Hide for today"
          title="Hide for today"
          data-testid="daily-wheel-close"
        >
          <X size={14} />
        </button>
      )}
      <div className="relative p-5 flex flex-col sm:flex-row items-center gap-5">

        {/* Wheel */}
        <div className="relative flex-shrink-0" data-testid="wheel-svg">
          <svg viewBox="0 0 200 200" width={160} height={160} className="drop-shadow-[0_8px_24px_rgba(251,191,36,0.3)]">
            <g style={{ transform: `rotate(${rotation}deg)`, transformOrigin: "100px 100px", transition: spinning ? "transform 3s cubic-bezier(0.2, 0.8, 0.2, 1)" : "none" }}>
              {wedges.map((w, i) => {
                const start = i * slice;
                const end = start + slice;
                const rad1 = (start - 90) * Math.PI / 180;
                const rad2 = (end - 90) * Math.PI / 180;
                const x1 = 100 + 90 * Math.cos(rad1);
                const y1 = 100 + 90 * Math.sin(rad1);
                const x2 = 100 + 90 * Math.cos(rad2);
                const y2 = 100 + 90 * Math.sin(rad2);
                const large = slice > 180 ? 1 : 0;
                const pathD = `M 100 100 L ${x1} ${y1} A 90 90 0 ${large} 1 ${x2} ${y2} Z`;
                const midRad = ((start + end) / 2 - 90) * Math.PI / 180;
                const tx = 100 + 55 * Math.cos(midRad);
                const ty = 100 + 55 * Math.sin(midRad);
                const rotText = (start + end) / 2;
                return (
                  <g key={w.id}>
                    <path d={pathD} fill={w.color} stroke="rgba(255,255,255,0.5)" strokeWidth={1.5} />
                    <text
                      x={tx} y={ty}
                      textAnchor="middle" dominantBaseline="middle"
                      fill="#ffffff" fontSize={w.id === "w100" ? 8 : 13} fontWeight="bold"
                      transform={`rotate(${rotText}, ${tx}, ${ty})`}
                      style={{ textShadow: "0 1px 2px rgba(0,0,0,0.6)" }}
                    >
                      {w.label}
                    </text>
                  </g>
                );
              })}
              {/* Center cap */}
              <circle cx={100} cy={100} r={18} fill="#1F2937" stroke="#FBBF24" strokeWidth={2} />
              <text x={100} y={100} textAnchor="middle" dominantBaseline="middle" fill="#FBBF24" fontSize={16} fontWeight="bold">★</text>
            </g>
            {/* Pointer (stays fixed at top) */}
            <polygon points="100,6 92,24 108,24" fill="#F59E0B" stroke="#ffffff" strokeWidth={1.5} />
          </svg>
        </div>

        {/* Text + CTA */}
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-1 flex-wrap">
            <Gift size={16} className="text-amber-300" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">Daily Free Spin</span>
            {status.streak > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30" data-testid="wheel-streak">
                🔥 {status.streak}-day streak
              </span>
            )}
          </div>
          <h3 className="text-xl font-fredoka font-bold text-white mb-1">Spin the Star Wheel</h3>
          <p className="text-white/60 text-xs mb-2">Every day = one free spin. Win 5 to <span className="text-amber-300 font-bold">100 Stars</span>!</p>

          {/* 7-day streak progress bar (always visible) */}
          <div className="mb-3" data-testid="streak-progress">
            <div className="flex items-center gap-1.5 mb-1">
              {[1, 2, 3, 4, 5, 6, 7].map(i => {
                const filled = (status.streak % 7 || (status.streak > 0 && status.streak % 7 === 0 ? 7 : 0)) >= i;
                const isSeven = i === 7;
                return (
                  <div key={i}
                    className={`h-2 flex-1 rounded-full transition-all ${filled ? (isSeven ? "bg-gradient-to-r from-amber-400 to-orange-500 shadow-[0_0_8px_rgba(251,191,36,0.7)]" : "bg-emerald-400") : "bg-white/10"}`}
                    data-testid={`streak-dot-${i}`} />
                );
              })}
            </div>
            <p className="text-[10px] text-white/50 text-center sm:text-left">
              {status.streak >= 7 && status.streak % 7 === 0 && !result?.streak_bonus ? (
                <span className="text-amber-300 font-bold">🔥 Today's the day! +200⭐ bonus on your next spin!</span>
              ) : status.streak > 0 && status.streak % 7 > 0 ? (
                <>🔥 <span className="text-orange-300 font-bold">{7 - (status.streak % 7)}</span> more day{(7 - (status.streak % 7)) === 1 ? "" : "s"} for <span className="text-amber-300 font-bold">+200⭐ bonus</span> & Week Warrior badge</>
              ) : (
                <>Hit a 7-day streak to unlock <span className="text-amber-300 font-bold">+200⭐ bonus</span> & the <span className="text-orange-300 font-bold">Week Warrior</span> badge</>
              )}
            </p>
          </div>

          {result?.already && !spinning ? (
            <div className="flex flex-col items-center sm:items-start gap-2">
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-sm font-bold" data-testid="wheel-already-spun">
                <Check size={14} /> You won <span className="text-amber-300">+{result.stars_won}⭐</span> today!
              </div>
              <p className="text-white/40 text-[11px]">Come back tomorrow for another spin 🌙</p>
            </div>
          ) : result && !result.already ? (
            <div className="flex flex-col items-center sm:items-start gap-2" data-testid="wheel-result">
              <div className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white font-bold text-base shadow-lg ${result.is_jackpot ? "bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 animate-pulse" : "bg-gradient-to-r from-amber-500 to-orange-500"}`}>
                <Sparkles size={16} /> You won {result.label}!
              </div>
              {result.streak_bonus > 0 && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 via-amber-500 to-red-500 text-white font-fredoka font-bold shadow-lg shadow-amber-500/40 animate-pulse" data-testid="streak-bonus-banner">
                  🔥 {result.streak_after}-Day Streak Bonus! +200⭐
                </div>
              )}
              {result.streak_badge_granted && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/20 border border-purple-500/40 text-purple-300 text-xs font-bold" data-testid="week-warrior-banner">
                  👑 NEW BADGE: Week Warrior unlocked!
                </div>
              )}
              <p className="text-white/50 text-[11px]">Stars added to your wallet 🌟</p>
            </div>
          ) : (
            <button
              onClick={spin}
              disabled={spinning || !status.can_spin}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white font-fredoka font-bold text-sm shadow-lg shadow-amber-500/30 hover:shadow-xl hover:shadow-amber-500/50 hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="spin-wheel-btn"
            >
              {spinning ? (<><Loader2 size={14} className="animate-spin" /> Spinning...</>) : (<><Star size={14} fill="currentColor" /> SPIN NOW, Free!</>)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyStarWheel;
